import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { Dashboard, ContentFilters, Widget } from '../models/core.models';

import { activateFilters } from '../actions/feed.actions';

import {
  getContentFiltersForPage,
  selectCurrentObject,
  selectCurrentObjectCacheEnabled,
  selectWidgetsLanguageId,
} from '../reducers';

import { injectFetchWidgetData } from 'src/app/api/queries/widget.queries';

import {
  tap,
  map,
  scan,
  take,
  Subject,
  finalize,
  interval,
  startWith,
  takeUntil,
  takeWhile,
  firstValueFrom,
  BehaviorSubject,
  distinctUntilChanged,
} from 'rxjs';

// * 'normal' = the Dashboard's own page view, 'presentation' = Presentation Mode.
// * Runtime-only, and unrelated to any flag named some form of "edit mode":
// * neither the Feed's compact widget list nor `object.user_settings.edit_mode`
// * (which editing tools the cards show).
type DashboardMode = 'normal' | 'presentation';

@Injectable()
export class DashboardAutoRefreshManagerService {
  private secondsLeftSubject = new BehaviorSubject<number | null>(null);
  secondsLeft$ = this.secondsLeftSubject.asObservable();
  private cacheEnabledSubject = new BehaviorSubject<boolean>(false);
  cacheEnabled$ = this.cacheEnabledSubject.asObservable();
  // * "a presentation refresh pass is in flight"
  private reloadingSubject = new BehaviorSubject<boolean>(false);
  reloading$ = this.reloadingSubject.asObservable();

  private readonly fetchWidgetData = injectFetchWidgetData();

  // * Presentation Mode's widget list, handed over by the presentation component:
  // * a service can't hold a query hook, and the component already owns the list
  private presentationWidgets: Widget[] = [];

  private stop$ = new Subject<void>();
  private currentMode: DashboardMode = 'normal';
  private isRunning = false;
  private settingsSubscription: any = null;

  constructor(
    private store: Store,
    private destroyRef: DestroyRef,
  ) {
    this.store
      .select(selectCurrentObjectCacheEnabled)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((cacheEnabled) => {
          this.cacheEnabledSubject.next(cacheEnabled);
        }),
      )
      .subscribe();
  }

  setNormalMode(): void {
    this.currentMode = 'normal';
  }

  setPresentationMode(): void {
    this.currentMode = 'presentation';
  }

  /** Presentation Mode's widget list (placeholders already excluded). */
  setPresentationWidgets(widgets: Widget[]): void {
    this.presentationWidgets = widgets;
  }

  startAutoRefresh(): void {
    if (this.settingsSubscription) {
      console.warn('[Auto-reload] Already running, skipping start');
      return;
    }

    // * Monitor dashboard settings continuously
    this.settingsSubscription = this.store
      .select(selectCurrentObject)
      .pipe(
        map((dashboard) => ({
          is_auto_refresh: (dashboard as Dashboard)?.is_auto_refresh || false,
          auto_refresh_seconds:
            (dashboard as Dashboard)?.auto_refresh_seconds || 0,
        })),
        distinctUntilChanged(
          (a, b) =>
            a.is_auto_refresh === b.is_auto_refresh &&
            a.auto_refresh_seconds === b.auto_refresh_seconds,
        ),
        tap((settings) => {
          // * Setting Changed

          this.stopCountdown();

          // * Start new countdown if enabled
          if (settings.is_auto_refresh && settings.auto_refresh_seconds > 0) {
            this.startCountdown(settings.auto_refresh_seconds);
          } else {
            this.secondsLeftSubject.next(null);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private startCountdown(refreshSeconds: number): void {
    this.isRunning = true;

    const countdown$ = (seconds: number) =>
      interval(1000).pipe(
        startWith(0),
        scan((acc) => acc - 1, seconds),
        takeWhile((s) => s > 0, true), // * inclusive: emit 0 before completing
        tap((s) => this.secondsLeftSubject.next(s)),
        finalize(() => {
          // ! Only refresh if countdown completed naturally (not cancelled)
          // ! fixes reload when auto-refresh is stopped
          if (this.isRunning) {
            this.refreshWidgets();
          }
        }),
      );

    // ! recursive Countdown
    const runCountdown = () => {
      countdown$(refreshSeconds)
        .pipe(takeUntil(this.stop$))
        .subscribe({
          complete: () => {
            // ! check if still running before starting next cycle
            if (!this.isRunning) {
              return;
            }

            // ! reset Countdown display and start next cycle
            this.secondsLeftSubject.next(refreshSeconds);

            // ! setTimeout to break synchronous recursion
            setTimeout(() => {
              if (this.isRunning) {
                runCountdown();
              }
            }, 0);
          },
        });
    };

    runCountdown();
  }

  private stopCountdown(): void {
    if (this.isRunning) {
      this.isRunning = false;
      this.stop$.next();
      this.stop$ = new Subject<void>(); // Reset for next countdown
    }
  }

  stopAutoRefresh(): void {
    this.stopCountdown();
    this.secondsLeftSubject.next(null);

    // * Unsubscribe from settings monitoring
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
      this.settingsSubscription = null;
    }
  }

  private refreshWidgets(): void {
    if (this.currentMode === 'presentation') {
      this.refreshPresentationMode();
    } else {
      this.refreshNormalMode();
    }
  }

  private refreshNormalMode(): void {
    // * Reload current page widgets (with is_loaded check via activateFilters)
    this.store.dispatch(activateFilters());
  }

  private async refreshPresentationMode(): Promise<void> {
    try {
      // * 1. reload underlying Page Widgets (invalidation hits the active cards)
      this.store.dispatch(activateFilters());

      // * 2. Reload all presentation widgets (eager - no visibility check)
      const [languageId, cacheEnabled] = await Promise.all([
        firstValueFrom(
          this.store.select(selectWidgetsLanguageId).pipe(take(1)),
        ),
        firstValueFrom(
          this.store.select(selectCurrentObjectCacheEnabled).pipe(take(1)),
        ),
      ]);

      const widgets = this.presentationWidgets;

      if (widgets.length === 0) {
        console.warn('[Auto-reload] No widgets found in presentation mode!');
        return;
      }

      this.reloadingSubject.next(true);

      try {
        // ! Content filters are resolved per Dashboard Page: each Page can carry
        // ! its own period and Presentation Mode shows them all, so the global
        // ! `getContentFilters` (frozen at the Page selected before entering
        // ! Presentation Mode) would refresh every slide with the wrong period.
        // ! This is also the key the presentation cards observe, so the initial
        // ! prefetch and this refresh have to agree on it.
        const pageIds = [...new Set(widgets.map((w) => w.page_id ?? null))];
        const filtersByPage = new Map<number | null, ContentFilters>(
          await Promise.all(
            pageIds.map(
              async (pageId) =>
                [
                  pageId,
                  await firstValueFrom(
                    this.store
                      .select(getContentFiltersForPage(pageId))
                      .pipe(take(1)),
                  ),
                ] as [number | null, ContentFilters],
            ),
          ),
        );

        // * Fetch all widgets data in parallel, into the cache the cards observe
        await Promise.all(
          widgets.map((widget) =>
            this.fetchWidgetData({
              widgetId: widget.id,
              contentFilters: filtersByPage.get(widget.page_id ?? null),
              languageId: languageId as number,
              cacheEnabled,
            }).catch((err) => {
              console.warn(
                `[Auto-reload] Widget ${widget.id} (${widget.title}) failed:`,
                err,
              );
              return null;
            }),
          ),
        );
      } finally {
        this.reloadingSubject.next(false);
      }
    } catch (error) {
      console.warn('[Auto-reload] Presentation mode refresh failed:', error);
      this.reloadingSubject.next(false);
    }
  }
}
